'use client'
import { useGetCompanyProductFamilyQuery } from '@/app/globalRedux/api/filter/companySearchSlice'
import {
  useGetCompanySearchQuery,
  useGetProductSearchQuery,
} from '@/app/globalRedux/api/searchSlice'
import { setSearchInitialLoad } from '@/app/globalRedux/dataSlice'
import { setLanguages } from '@/app/globalRedux/filter/newsFilter'
import { setListedProductFamily } from '@/app/globalRedux/filterList/companyFilterList'
import {
  setProductListedBuyerSupplier,
  setProductListedProductFamily,
  setProductListedProductFamilyCount,
} from '@/app/globalRedux/filterList/productFilterList'
import { AppDispatch, RootState } from '@/app/globalRedux/store'
import {
  CompanyResponse,
  ProductFamilyListResponse,
} from '@/app/types/companySearchType'
import { ProductSearchResponse } from '@/app/types/productSearchType'
import { languageTransition } from '@/app/utils/language'
import { convertNaNToOne } from '@/lib/formatUtils'
import { getCountryList } from '@/services/generalDataServices'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import RightAdsPanel from '../../components/AdsFructidor/RightAdsPanel/RightAdsPanel'
import CompanyListingSkeleton from '../../components/Skeletons/CompanyListingSkeleton'

const SearchResultLayout = ({ children }: { children: React.ReactNode }) => {
  const params = useParams()
  const router = useRouter()
  const searchParams = useSearchParams()
  const dispatch: AppDispatch = useDispatch()
  const commonFilter = useSelector((state: RootState) => state.commonFilter)
  const companyFilter = useSelector((state: RootState) => state.companyFilter)
  const productsFilter = useSelector((state: RootState) => state.productFilter)

  const { countriesList, search_initial_load } = useSelector(
    (state: RootState) => state.constantData
  )

  useEffect(() => {
    if (countriesList.length <= 0) {
      dispatch(getCountryList())
    }
  }, [])
  const { data: companyDataNameBased, isFetching: isCompanyDataFetching } =
    useGetCompanySearchQuery<{
      data: CompanyResponse
      isFetching: boolean
      isLoading: boolean
      isError: boolean
      isSuccess: boolean
    }>(
      {
        search_on: 'company_name',
        companyFilter: companyFilter,
        commonFilter: commonFilter,
        productFilter: productsFilter,
        lang: String(params?.lng),
        pageNo: convertNaNToOne(Number(params?.companyPageNo?.[0] || 1)),
      },
      {
        skip: companyFilter.isRouteUpdating,
      }
    )

  const {
    data: companyDataProductBased,
    isFetching: isCompanyProductDataFetching,
  } = useGetCompanySearchQuery<{
    data: CompanyResponse
    isFetching: boolean
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
  }>(
    {
      search_on: 'product',
      companyFilter: companyFilter,
      commonFilter: commonFilter,
      productFilter: productsFilter,
      lang: String(params?.lng),
      pageNo: convertNaNToOne(Number(params?.companyPageNo?.[0] || 1)),
    },
    {
      skip: companyFilter.isRouteUpdating,
    }
  )

  const { data: productData, isFetching: isProductDataFetching } =
    useGetProductSearchQuery<{
      data: ProductSearchResponse
      isFetching: boolean
      isError: boolean
      isLoading: boolean
      isSuccess: boolean
    }>({
      productsFilter: productsFilter,
      commonFilter: commonFilter,
      pageNo: convertNaNToOne(Number(params?.productPageNO?.[0]) || 1),
    })

  const { data: productfamilyData, isLoading: isProductFamilyLoading } =
    useGetCompanyProductFamilyQuery<{
      data: ProductFamilyListResponse
      isLoading: boolean
      isError: boolean
    }>(productsFilter.sector)

  useEffect(() => {
    if (productfamilyData && !isProductFamilyLoading) {
      dispatch(setListedProductFamily(productfamilyData.product_families))
      dispatch(
        setProductListedProductFamily(productfamilyData.product_families)
      )
    }
  }, [productfamilyData])

  useEffect(() => {
    if (productData) {
      dispatch(
        setProductListedBuyerSupplier([
          ...(productData?.facet_count?.activities ?? []),
          ...(productData?.facet_count?.activities ?? []),
        ])
      )
      dispatch(
        setProductListedProductFamilyCount([
          ...(productData?.facet_count?.product_family ?? []),
        ])
      )
    }
  }, [isProductDataFetching, productData])

  useEffect(() => {
    const queryLang = searchParams.get('newslng')
    const routeLang = params?.lng
    const selectedLang = queryLang || routeLang

    // Check for newslang filter applied if not use the lng
    const language =
      languageTransition.find(
        (e) => e.path === selectedLang || e.language === selectedLang
      )?.language ?? 'English'

    dispatch(setLanguages(language))
  }, [searchParams, params?.lng])

  useEffect(() => {
    if (!isCompanyDataFetching && !isCompanyProductDataFetching) {
      dispatch(setSearchInitialLoad(false))
      if (
        !companyDataProductBased?.count &&
        companyDataNameBased?.count &&
        search_initial_load
      ) {
        router.push(
          `/${params?.lng}/search/company-tab?searchTerm=${searchParams.get('searchTerm') ?? ''}`
        )
      }
    }
  }, [companyDataProductBased, companyDataNameBased])

  return (
    <div>
      <div className="flex justify-center mt-[30px] mobile:flex-col">
        <div className="w-[1280px] flex justify-between mb-[30px] mobile-width mobile:flex-col">
          <div>
            {isCompanyDataFetching || isCompanyProductDataFetching ? (
              <CompanyListingSkeleton />
            ) : (
              children
            )}
          </div>
          <RightAdsPanel />
        </div>
      </div>
    </div>
  )
}

export default SearchResultLayout
